import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import '../styles/AdminPages.css';

const CrudPage = ({ 
  title, 
  subtitle,
  api, 
  columns, 
  formFields,
  idField = 'id',
  searchField = null,
  addUrl = null,
  viewUrl = null,
  editUrl = null,
  disableCreate = false,
  disableEdit = false,
  disableDelete = false
}) => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [formError, setFormError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize] = useState(20);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch all records (up to 500) for client-side pagination
      const response = await api.getAll({ start: 0, pageSize: 500 });
      const items = response?.data?.[0] || response?.data || response || [];
      setData(Array.isArray(items) ? items : []);
      setError('');
    } catch (err) {
      console.error('Error fetching data:', err);
      const errorDetail = err.status ? `[${err.status}] ` : '';
      const errorMessage = err.message || 'Unknown error';
      setError(`Failed to load data: ${errorDetail}${errorMessage}`);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const initFormData = () => {
    const initial = {};
    formFields.forEach(field => {
      initial[field.name] = field.defaultValue || '';
    });
    return initial;
  };

  const handleAdd = () => {
    setEditingItem(null);
    setFormData(initFormData());
    setFormError('');
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    const data = {};
    formFields.forEach(field => {
      if (field.getValue) {
        data[field.name] = field.getValue(item) ?? '';
      } else {
        data[field.name] = item[field.name] ?? '';
      }
    });
    setFormData(data);
    setFormError('');
    setShowModal(true);
  };

  const handleDelete = async (item) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'You will not be able to recover this item!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) return;

    try {
      await api.delete(item[idField]);
      Swal.fire({
        title: 'Deleted!',
        text: 'The item has been deleted.',
        icon: 'success',
        timer: 1500,
        showConfirmButton: false
      });
      fetchData();
    } catch (err) {
      console.error('Error deleting:', err);
      const errorStatus = err.status ? `[${err.status}] ` : '';
      const errorMessage = err.message || 'Unknown error occurred';
      Swal.fire({
        title: 'Delete Failed',
        html: `<div style="text-align:left;font-size:14px;"><strong>Error:</strong> ${errorStatus}${errorMessage}</div>`,
        icon: 'error'
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    const { errors, convertedData } = validateAndConvertData();
    
    if (errors.length > 0) {
      setFormError(errors.join('\n'));
      return;
    }

    try {
      if (editingItem) {
        await api.update(editingItem[idField], convertedData);
        Swal.fire({
          title: 'Updated!',
          text: 'The item has been updated successfully.',
          icon: 'success',
          timer: 1500,
          showConfirmButton: false
        });
      } else {
        await api.create(convertedData);
        Swal.fire({
          title: 'Created!',
          text: 'The item has been created successfully.',
          icon: 'success',
          timer: 1500,
          showConfirmButton: false
        });
      }
      setShowModal(false);
      fetchData();
    } catch (err) {
      console.error('Error saving:', err);
      const errorStatus = err.status ? `[${err.status}] ` : '';
      const errorMessage = err.message || err.response?.data?.message || 'Unknown error occurred';
      setFormError(`${errorStatus}${errorMessage}`);
    }
  };

  const handleInputChange = (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
  };

  const validateAndConvertData = () => {
    const errors = [];
    const convertedData = {};

    formFields.forEach(field => {
      const value = formData[field.name];
      const fieldLabel = field.label || field.name;

      if (field.required && (value === '' || value === null || value === undefined)) {
        errors.push(`${fieldLabel} is required`);
        return;
      }

      if (value === '' || value === null || value === undefined) {
        convertedData[field.name] = null;
        return;
      }

      switch (field.type) {
        case 'number':
          const numValue = Number(value);
          if (isNaN(numValue)) {
            errors.push(`${fieldLabel} must be a valid number`);
          } else {
            convertedData[field.name] = numValue;
          }
          break;

        case 'integer':
          const intValue = parseInt(value, 10);
          if (isNaN(intValue) || !Number.isInteger(Number(value))) {
            errors.push(`${fieldLabel} must be a valid integer`);
          } else {
            convertedData[field.name] = intValue;
          }
          break;

        case 'decimal':
        case 'float':
          const floatValue = parseFloat(value);
          if (isNaN(floatValue)) {
            errors.push(`${fieldLabel} must be a valid decimal number`);
          } else {
            convertedData[field.name] = floatValue;
          }
          break;

        case 'checkbox':
        case 'boolean':
          convertedData[field.name] = Boolean(value);
          break;

        case 'date':
          if (value) {
            const dateValue = new Date(value);
            if (isNaN(dateValue.getTime())) {
              errors.push(`${fieldLabel} must be a valid date`);
            } else {
              convertedData[field.name] = dateValue.toISOString();
            }
          } else {
            convertedData[field.name] = null;
          }
          break;

        case 'email':
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value)) {
            errors.push(`${fieldLabel} must be a valid email address`);
          } else {
            convertedData[field.name] = String(value).trim();
          }
          break;

        case 'url':
          try {
            new URL(value);
            convertedData[field.name] = String(value).trim();
          } catch {
            errors.push(`${fieldLabel} must be a valid URL`);
          }
          break;

        default:
          convertedData[field.name] = String(value);
          break;
      }
    });

    return { errors, convertedData };
  };

  const filteredData = searchTerm && searchField
    ? data.filter(item => 
        String(item[searchField] || '').toLowerCase().includes(searchTerm.toLowerCase())
      )
    : data;

  const totalPages = Math.ceil(filteredData.length / pageSize) || 1;
  const safePage = Math.min(page, totalPages - 1);
  const paginatedData = filteredData.slice(safePage * pageSize, (safePage + 1) * pageSize);
  const startIndex = filteredData.length > 0 ? safePage * pageSize + 1 : 0;
  const endIndex = Math.min((safePage + 1) * pageSize, filteredData.length);

  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  const renderField = (field) => {
    const value = formData[field.name] ?? '';

    switch (field.type) {
      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            required={field.required}
          >
            <option value="">Select {field.label}</option>
            {field.options?.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        );
      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            required={field.required}
            placeholder={field.placeholder}
          />
        );
      case 'number':
      case 'decimal':
      case 'float':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            required={field.required}
            placeholder={field.placeholder}
            step={field.step || 'any'}
          />
        );
      case 'integer':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            required={field.required}
            placeholder={field.placeholder}
            step="1"
          />
        );
      case 'date':
        return (
          <input
            type="date"
            value={value ? value.slice(0, 10) : ''}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            required={field.required}
          />
        );
      case 'datetime':
        return (
          <input
            type="datetime-local"
            value={value ? value.slice(0, 16) : ''}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            required={field.required}
          />
        );
      case 'bool-toggle':
        return (
          <div className="bool-toggle">
            <button
              type="button"
              className={`toggle-btn ${value === true ? 'active yes' : ''}`}
              onClick={() => handleInputChange(field.name, true)}
            >
              Yes
            </button>
            <button
              type="button"
              className={`toggle-btn ${value === false ? 'active no' : ''}`}
              onClick={() => handleInputChange(field.name, false)}
            >
              No
            </button>
          </div>
        );
      case 'checkbox':
      case 'boolean':
        return (
          <input
            type="checkbox"
            checked={Boolean(value)}
            onChange={(e) => handleInputChange(field.name, e.target.checked)}
          />
        );
      case 'email':
        return (
          <input
            type="email"
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            required={field.required}
            placeholder={field.placeholder || 'email@example.com'}
          />
        );
      case 'url':
        return (
          <input
            type="url"
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            required={field.required}
            placeholder={field.placeholder || 'https://example.com'}
          />
        );
      default:
        return (
          <input
            type={field.type || 'text'}
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            required={field.required}
            placeholder={field.placeholder}
          />
        );
    }
  };

  const renderCellValue = (item, col) => {
    const value = item[col.field];
    
    if (col.render) {
      return col.render(value, item);
    }
    
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (col.type === 'date' && value) {
      return new Date(value).toLocaleDateString();
    }
    if (typeof value === 'object') return JSON.stringify(value);
    
    return String(value).length > 50 ? String(value).slice(0, 50) + '...' : value;
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button 
            onClick={() => navigate(-1)} 
            style={{
              background: '#f1f5f9',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 15px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              color: '#475569',
              fontWeight: '500',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => e.currentTarget.style.background = '#e2e8f0'}
            onMouseOut={(e) => e.currentTarget.style.background = '#f1f5f9'}
          >
            <span style={{ fontSize: '16px' }}>←</span> Back
          </button>
          <div>
            <h1>{title}</h1>
            <p>{subtitle}</p>
          </div>
        </div>
        {!disableCreate && (
          <button className="admin-btn primary" onClick={addUrl ? () => navigate(addUrl) : handleAdd}>
            + Add New
          </button>
        )}
      </div>

      <div className="admin-toolbar">
        {searchField && (
          <input
            type="text"
            className="admin-search"
            placeholder={`Search by ${searchField}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        )}
      </div>

      <div className="admin-table-card">
        {error && (
          <div className="admin-form-error" style={{ margin: '20px', borderRadius: '8px' }}>
            {error}
          </div>
        )}
        
        <table className="admin-table">
          <thead>
            <tr>
              {columns.map((col, index) => (
                <th key={index}>{col.header}</th>
              ))}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr className="admin-loading-row">
                <td colSpan={columns.length + 1}>
                  <div className="admin-table-loading">
                    <div className="admin-spinner"></div>
                    <span>Loading...</span>
                  </div>
                </td>
              </tr>
            ) : paginatedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 1} className="admin-empty">
                  No data found
                </td>
              </tr>
            ) : (
              paginatedData.map((item, index) => (
                <tr key={item[idField] || index}>
                  {columns.map((col, colIndex) => (
                    <td key={colIndex}>{renderCellValue(item, col)}</td>
                  ))}
                  <td>
                    <div className="admin-actions">
                      {viewUrl && (
                        <button className="admin-btn-sm view" onClick={() => navigate(`${viewUrl}/${item[idField]}`)}>
                          View
                        </button>
                      )}
                      {!disableEdit && (editUrl ? (
                        <button className="admin-btn-sm edit" onClick={() => navigate(editUrl.includes(':id') ? editUrl.replace(':id', item[idField]) : `${editUrl}/${item[idField]}`)}>
                          Edit
                        </button>
                      ) : (
                        <button className="admin-btn-sm edit" onClick={() => handleEdit(item)}>
                          Edit
                        </button>
                      ))}
                      {!disableDelete && (
                        <button className="admin-btn-sm delete" onClick={() => handleDelete(item)}>
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {!loading && filteredData.length > 0 && (
          <div className="admin-pagination">
            <div className="admin-pagination-info">
              Showing {startIndex}-{endIndex} of {filteredData.length} items (Page {page + 1} of {totalPages})
            </div>
            <div className="admin-pagination-btns">
              <button 
                className="admin-pagination-btn"
                disabled={page === 0}
                onClick={() => setPage(0)}
              >
                First
              </button>
              <button 
                className="admin-pagination-btn"
                disabled={page === 0}
                onClick={() => setPage(p => p - 1)}
              >
                Previous
              </button>
              <button 
                className="admin-pagination-btn"
                disabled={page >= totalPages - 1}
                onClick={() => setPage(p => p + 1)}
              >
                Next
              </button>
              <button 
                className="admin-pagination-btn"
                disabled={page >= totalPages - 1}
                onClick={() => setPage(totalPages - 1)}
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <div className="admin-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>{editingItem ? 'Edit' : 'Add'} {title.replace(/s$/, '')}</h2>
              <button className="admin-modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              {formError && (
                <div className="admin-form-error" style={{ whiteSpace: 'pre-line' }}>
                  {formError}
                </div>
              )}
              {formFields.map((field, index) => (
                <div key={index} className="admin-form-group">
                  <label>{field.label}</label>
                  {renderField(field)}
                </div>
              ))}
              <div className="admin-modal-actions">
                <button type="button" className="admin-btn secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="admin-btn primary">
                  {editingItem ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CrudPage;
